/**
 * Sets translated View / Edit / Delete (etc.) tooltips on list-table action buttons
 * from their Nioicon class, so copy-pasted btn-groups get labels without editing every Vue file.
 *
 * Only table action cells (.table-action, .nk-tb-col-tools, .btn-group / .doc-action-btns
 * inside a table td). Never header / Home widgets / theme / search / moon.
 */
const ICON_TIPS = [
    { test: /\bni-eye(?:-fill|-alt)?\b/, key: 'general.view', fallback: 'View' },
    { test: /\bni-pen(?:cil)?(?:-fill|-alt)?\b|\bni-edit(?:-fill|-alt)?\b/, key: 'general.edit', fallback: 'Edit' },
    { test: /\bni-trash(?:-fill|-alt)?\b/, key: 'general.delete', fallback: 'Delete' },
    { test: /\bni-printer(?:-fill)?\b/, key: 'general.print', fallback: 'Print' },
    { test: /\bni-file-pdf(?:-fill)?\b/, key: 'general.generate_pdf', fallback: 'Generate PDF' },
    { test: /\bni-download(?:-fill|-cloud)?\b/, key: 'general.export', fallback: 'Download' },
    { test: /\bni-plus(?:-circle|-c|-sm)?(?:-fill)?\b/, key: 'general.add', fallback: 'Add' }
];

const SKIP_CLOSEST = [
    '.nk-header',
    '.sip-admin-header',
    '.sip-home',
    '.sip-theme-picker',
    '.sip-icon-btn',
    '.nk-sidebar',
    '.nk-menu',
    '.search-overlay',
    '.dropdown-menu',
    '.sip-row-actions',
    '.sip-row-actions__menu',
    '.nk-block-tools',
    '.sip-pager',
    '[data-sip-widget]',
    '.sip-dash-widget'
].join(', ');

const ACTION_CELL = 'td.table-action, th.table-action, td.nk-tb-col-tools, th.nk-tb-col-tools, .nk-tb-col-tools';

const SCAN_SELECTOR = [
    '.table td.table-action .btn',
    '.table td.table-action button',
    '.table td.table-action a.btn',
    '.table td.table-action .doc-action-btn',
    '.table td .btn-group > .btn',
    '.table td .btn-group > a',
    '.table td .btn-group > button',
    '.table td .doc-action-btns > .btn',
    '.table td .doc-action-btns > button',
    '.table td .doc-action-btns > a',
    '.table-responsive table td .btn-group > .btn',
    '.table-responsive table td .btn-group > a',
    '.table-responsive table td .doc-action-btns > .btn',
    '.table-responsive table td .doc-action-btns > button',
    '.table-responsive table td.table-action .btn',
    '.table-responsive table td.table-action button',
    '.nk-tb-col-tools .btn-group > .btn',
    '.nk-tb-col-tools .btn',
    '.nk-tb-col-tools button',
    '.sip-row-actions__icon-btn'
].join(',');

function t(key, fallback) {
    if (window._get && window.i18n) {
        const value = window._get(window.i18n, key);
        if (value) {
            return value;
        }
    }
    return fallback;
}

function iconClass(btn) {
    const icon = btn.querySelector('.icon, .ni, em, i');
    const bits = [btn.className || ''];
    if (icon) {
        bits.push(icon.getAttribute('class') || '');
        bits.push(icon.getAttribute('data-icon') || '');
    }
    return bits.join(' ');
}

function tipFor(btn) {
    const cls = iconClass(btn);
    for (let i = 0; i < ICON_TIPS.length; i++) {
        if (ICON_TIPS[i].test.test(cls)) {
            return t(ICON_TIPS[i].key, ICON_TIPS[i].fallback);
        }
    }
    if (/\bbtn-danger\b/.test(btn.className) || /doc-action-btn-danger/.test(btn.className)) {
        return t('general.delete', 'Delete');
    }
    return '';
}

function isTableActionBtn(btn) {
    if (!btn || btn.nodeType !== 1 || !btn.closest) {
        return false;
    }
    if (btn.classList.contains('sip-row-actions__icon-btn')) {
        return true;
    }
    if (btn.closest(SKIP_CLOSEST)) {
        return false;
    }
    if (btn.classList.contains('sip-row-actions__btn') || btn.hasAttribute('data-sip-row-src')) {
        return false;
    }
    if (btn.closest('.sip-row-actions__menu')) {
        return false;
    }
    if (btn.closest(ACTION_CELL)) {
        return true;
    }
    const td = btn.closest('table td, table th, .table td, .table th');
    if (!td) {
        return false;
    }
    return !!(btn.closest('.btn-group, .doc-action-btns') || td.classList.contains('table-action'));
}

function alreadyLabeled(btn) {
    return !!(
        btn.getAttribute('title')
        || btn.getAttribute('data-original-title')
        || btn.getAttribute('aria-label')
    );
}

function actionLabelIsVisible(btn) {
    const label = btn && btn.querySelector && btn.querySelector('.sip-row-actions__btn-label');
    if (!label) {
        return false;
    }
    if (window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches) {
        return true;
    }
    if (!btn.isConnected) {
        return false;
    }
    try {
        const style = window.getComputedStyle(label);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    } catch (e) {
        return false;
    }
}

function clearBtnTip(btn) {
    if (!btn) {
        return;
    }
    try {
        if (window.$ && window.$.fn && window.$.fn.tooltip) {
            window.$(btn).tooltip('hide');
            window.$(btn).tooltip('dispose');
        }
    } catch (e) { /* not inited */ }
    btn.removeAttribute('title');
    btn.removeAttribute('data-original-title');
    btn.removeAttribute('data-tip');
}

function applyTip(btn) {
    if (!btn || btn.nodeType !== 1) {
        return;
    }
    if (!isTableActionBtn(btn)) {
        return;
    }
    if (btn.classList.contains('sip-row-actions__icon-btn') && actionLabelIsVisible(btn)) {
        clearBtnTip(btn);
        if (btn.dataset) {
            btn.dataset.sipActionTip = 'labeled';
        }
        return;
    }
    if (btn.dataset && btn.dataset.sipActionTip === 'labeled') {
        delete btn.dataset.sipActionTip;
    }
    if (btn.dataset.sipActionTip) {
        return;
    }
    if (alreadyLabeled(btn) && !btn.classList.contains('sip-row-actions__icon-btn')) {
        btn.dataset.sipActionTip = 'skip';
        return;
    }
    const label = tipFor(btn) || btn.getAttribute('title') || btn.getAttribute('aria-label') || '';
    if (!label) {
        return;
    }
    btn.dataset.sipActionTip = '1';
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
    btn.setAttribute('data-tip', label);
    if (window.$ && window.$.fn && window.$.fn.tooltip) {
        try {
            window.$(btn).tooltip('dispose');
        } catch (e) { /* not inited */ }
        window.$(btn).tooltip({
            container: 'body',
            placement: btn.classList.contains('sip-row-actions__icon-btn') ? 'top' : 'left',
            trigger: 'hover',
            title: label
        });
    }
}

export function destroyActionTips() {
    try {
        if (window.$ && window.$.fn && window.$.fn.tooltip) {
            window.$('.sip-row-actions__icon-btn[data-sip-action-tip="1"]').tooltip('hide');
            window.$('[data-sip-action-tip="1"]:not(.sip-row-actions__icon-btn)').tooltip('hide');
            window.$('[data-sip-action-tip="1"]:not(.sip-row-actions__icon-btn)').tooltip('dispose');
            window.$('body > .tooltip').tooltip('hide');
            window.$('body > .tooltip').remove();
        }
    } catch (e) { /* plugin not ready */ }
    const leftovers = document.querySelectorAll('body > .tooltip, body > .popover, .tooltip.show');
    leftovers.forEach(function (el) {
        if (el && el.parentNode) {
            el.parentNode.removeChild(el);
        }
    });
}

function onOverlayClose(e) {
    const t = e && e.target;
    if (!t || !t.closest) {
        return;
    }
    if (t.closest('.search-overlay, .doc-bottom-sheet-overlay, .nk-sidebar-overlay, .loading-overlay, .modal, .sip-theme-panel')) {
        destroyActionTips();
    }
}

function hideVisibleTips() {
    try {
        if (window.$ && window.$.fn && window.$.fn.tooltip) {
            window.$('[data-sip-action-tip="1"]').tooltip('hide');
            window.$('body > .tooltip').remove();
        }
    } catch (e) { /* plugin not ready */ }
}

function bindLifecycle() {
    if (bindLifecycle.done) {
        return;
    }
    bindLifecycle.done = true;
    document.addEventListener('click', onOverlayClose, true);
    document.addEventListener('hidden.bs.modal', destroyActionTips);
    document.addEventListener('hide.bs.modal', destroyActionTips);
    document.addEventListener('scroll', function (e) {
        const t = e && e.target;
        if (t && t.classList && t.classList.contains('table-responsive')) {
            hideVisibleTips();
        }
    }, true);
    if (window.$) {
        window.$(document).on('hidden.bs.modal hide.bs.modal hidden.bs.dropdown', destroyActionTips);
    }
}

export function scanActionTips(root) {
    bindLifecycle();
    const scope = root && root.querySelectorAll ? root : document;
    if (scope !== document && scope.closest && scope.closest('.sip-home, .nk-header, .sip-admin-header')) {
        return;
    }
    scope.querySelectorAll('[data-sip-row-src], .sip-row-actions__btn').forEach(function (btn) {
        if (!btn) {
            return;
        }
        try {
            if (window.$ && window.$.fn && window.$.fn.tooltip) {
                window.$(btn).tooltip('hide');
                window.$(btn).tooltip('dispose');
            }
        } catch (e) { /* not inited */ }
        btn.removeAttribute('title');
        btn.removeAttribute('data-original-title');
        if (btn.dataset) {
            delete btn.dataset.sipActionTip;
        }
    });
    scope.querySelectorAll(SCAN_SELECTOR).forEach(applyTip);
}

export default {
    inserted: applyTip,
    componentUpdated: applyTip,
    unbind: function (el) {
        if (!el) {
            return;
        }
        try {
            if (window.$ && window.$.fn && window.$.fn.tooltip && el.dataset && el.dataset.sipActionTip === '1') {
                window.$(el).tooltip('hide');
                window.$(el).tooltip('dispose');
            }
        } catch (e) { /* gone */ }
    }
};
